//get all employees

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  CustomerFormData,
  ICustomer,
  getAllCustomers,
  getHistorySeller,
  HistorySellerResponse,
} from '../api/customers';
import { IEmployee } from '../api/employee';
// DTO cho customer
export interface CustomerDTO {
  id: string;
  seller: IEmployee;
  fullName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  hobbies: string;
  style: string;
  favoriteColor: string;
  notes: string;
  maritalStatus: string;
  spouseName: string;
  spouseBirthday: string;
  specialNotes: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  createdBy: IEmployee;
  phoneFamily: string;
  workAddress: string;
  orderCount: number;
  giftRequestCount: number;
}

// Chuyển đổi từ ICustomer sang CustomerDTO
export const parseCustomerDTO = (customer: ICustomer): CustomerDTO => ({
  id: customer.id,
  seller: customer.seller,
  fullName: customer.fullName,
  phoneNumber: customer.phoneNumber,
  email: customer.email,
  dateOfBirth: customer.dateOfBirth || '',
  gender: customer.gender || '',
  address: customer.address || '',
  hobbies: customer.hobbies || '',
  style: customer.style || '',
  favoriteColor: customer.favoriteColor || '',
  notes: customer.notes || '',
  maritalStatus: customer.maritalStatus || '',
  spouseName: customer.spouseName || '',
  spouseBirthday: customer.spouseBirthday || '',
  specialNotes: customer.specialNotes || '',
  createdAt: customer.createdAt || '',
  updatedAt: customer.updatedAt || '',
  deletedAt: customer.deletedAt || '',
  createdBy: customer.createdBy,
  phoneFamily: customer.phoneFamily || '',
  workAddress: customer.workAddress || '',
  orderCount: customer.orderCount || 0,
  giftRequestCount: customer.giftRequestCount || 0,
});

// Lấy danh sách customers (có phân trang, tìm kiếm)
export const useCustomers = ({
  page,
  limit,
  search,
  sellerId,
}: {
  page: number;
  limit: number;
  search: string;
  sellerId: string | null;
}) => {
  return useQuery<{
    customers: CustomerDTO[];
    total: number;
    pages: number;
    hasNextPage: boolean;
  }>({
    queryKey: ['customers', page, limit, search, sellerId],
    queryFn: async () => {
      const response = await getCustomers(page, limit, search, sellerId);
      const customers = response.items.map(parseCustomerDTO);
      return {
        customers,
        total: response.totalItems,
        pages: response.totalPages,
        hasNextPage: response.hasNextPage,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useAllCustomers = () => {
  return useQuery<ICustomer[]>({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await getAllCustomers();
      return response;
    },
  });
};
// Tạo customer
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CustomerFormData) => {
      const response = await createCustomer(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Tạo khách hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật customer
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: CustomerFormData;
    }) => {
      const response = await updateCustomer(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Cập nhật khách hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Xóa customer
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteCustomer(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Xóa khách hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useGetHistorySeller = (id: string | null) => {
  return useQuery<HistorySellerResponse[]>({
    queryKey: ['history-seller', id],
    queryFn: async () => {
      const response = await getHistorySeller(id);
      return response;
    },
    enabled: !!id,
  });
};
